/**
 * 芝柯 CC4 蓝牙打印机模块（Web Bluetooth API）
 * 适配东集PDA 安卓16 1.04/1.06 全版本兼容
 *
 * 使用限制：
 *  1. 需 HTTPS 或 localhost
 *  2. 仅 Android Chrome / Edge 支持
 *  3. requestDevice() 必须由用户手势触发
 */

// 芝柯 CC4 GATT UUID（官方文档）
var UUID_SERVICE = '49535343-fe7d-4ae5-8fa9-9fafd205e455'
var UUID_CHAR_WRITE = '49535343-8841-43f4-a8d4-ecbe34729bb3'
var UUID_CHAR_NOTIFY = '49535343-1e4d-4bd9-ba61-23c647249616'
// 设备名称前缀（官方示例使用 'CC3'，CC4 也匹配此前缀）
var DEVICE_NAME_PREFIX = 'CC'

// 缓存
var cachedDevice = null
var cachedCharWrite = null

/** 是否支持 Web Bluetooth */
export function isSupported() {
  return !!(navigator.bluetooth && window.isSecureContext)
}

/**
 * 检测不支持的具体原因（供 UI 展示详细提示）
 * @returns {string} 不支持的描述，支持时返回空字符串
 */
export function getUnsupportedReason() {
  if (!navigator.bluetooth) {
    return "当前浏览器不支持蓝牙功能，请使用 Chrome 或 Edge 浏览器";
  }
  if (!window.isSecureContext) {
    return "当前页面非 HTTPS 安全连接，蓝牙功能需要 HTTPS 或 localhost 环境";
  }
  return "";
}

/** 点击时立即申请蓝牙连接，先于图片加载；统一返回 Promise 供页面捕获错误。 */
export function connectPrinter() {
  if (!isSupported()) return Promise.reject(new Error(getUnsupportedReason()))
  try {
    return getWriteCharacteristic()
  } catch (err) {
    return Promise.reject(err)
  }
}

/** 断开连接 */
export function disconnect() {
  if (cachedDevice && cachedDevice.gatt && cachedDevice.gatt.connected) {
    cachedDevice.gatt.disconnect()
  }
  cachedDevice = null
  cachedCharWrite = null
}

/**
 * 连接打印机 → 返回可写特征值
 *
 * 策略：
 *   1. 已连接 → 直接复用
 *   2. 缓存设备但断连 → 静默重连
 *   3. 重连失败 / 首次 → 提示重新点击
 */
function getWriteCharacteristic() {
  // 已连接 → 直接复用
  if (cachedDevice && cachedDevice.gatt && cachedDevice.gatt.connected && cachedCharWrite) {
    return Promise.resolve(cachedCharWrite)
  }

  // 缓存设备 → 静默重连
  if (cachedDevice && cachedDevice.gatt) {
    return cachedDevice.gatt.connect().then(function () {
      return cachedDevice.gatt.getPrimaryService(UUID_SERVICE)
    }).then(function (service) {
      return service.getCharacteristic(UUID_CHAR_WRITE)
    }).then(function (ch) {
      cachedCharWrite = ch
      return ch
    }).catch(function () {
      cachedDevice = null
      cachedCharWrite = null
      return Promise.reject(new Error('打印机连接已断开，请重新点击打印按钮'))
    })
  }

  // 首次连接 → 弹窗选设备
  return navigator.bluetooth.requestDevice({
    filters: [{ namePrefix: DEVICE_NAME_PREFIX }],
    optionalServices: [UUID_SERVICE]
  }).then(function (device) {
    cachedDevice = device
    device.addEventListener('gattserverdisconnected', function () {
      cachedCharWrite = null
    })
    return device.gatt.connect()
  }).then(function (server) {
    return server.getPrimaryService(UUID_SERVICE)
  }).then(function (service) {
    return service.getCharacteristic(UUID_CHAR_WRITE)
  }).then(function (ch) {
    cachedCharWrite = ch
    return ch
  })
}

/** Canvas 灰度二值化 → 1bit 位图（行式，MSB 优先） */
function canvasToBitmap(canvas) {
  var ctx = canvas.getContext('2d')
  var w = canvas.width
  var h = canvas.height
  var data = ctx.getImageData(0, 0, w, h).data
  var widthBytes = Math.ceil(w / 8)
  var bitmap = new Uint8Array(widthBytes * h)
  // 初始化为全白(0x00)，黑点置1
  for (var y = 0; y < h; y++) {
    for (var x = 0; x < w; x++) {
      var idx = (y * w + x) * 4
      var lum = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114
      if (data[idx + 3] > 128 && lum < 160) {
        bitmap[y * widthBytes + (x >> 3)] |= (0x80 >> (x & 7))
      }
    }
  }
  return { bitmap: bitmap, widthBytes: widthBytes, height: h }
}

/**
 * Canvas → ESC/POS 指令流（芝柯 CC4，GS v 0 光栅位图）
 * 新增：打印机编码强制设为GBK，清空残留缓冲区
 *
 * 指令序列：
 *   ESC @          — 初始化
 *   FS 38          — 选择GBK中文编码模式
 *   ESC @          — 二次初始化清空缓冲区
 *   GS v 0 m=0     — 光栅位图（普通模式）
 *   xL xH yL yH    — 宽(字节/行) 高(点数) little-endian
 *   [bitmap data]  — (widthBytes × height) 字节
 *   LF LF          — 走纸
 */
function canvasToEscpos(canvas) {
  var bm = canvasToBitmap(canvas)
  var wLo = bm.widthBytes & 0xff
  var wHi = (bm.widthBytes >> 8) & 0xff
  var hLo = bm.height & 0xff
  var hHi = (bm.height >> 8) & 0xff

  var header = new Uint8Array
  ([
    0x00, 0x00,                // 2字节唤醒探针，提前唤醒打印机，重置休眠定时器
    0x1b, 0x40,                // ESC @  第一次初始化清空缓冲区
    0x1c, 0x26,                // FS &  进入中文模式
    0x1c, 0x43, 0x01,          // FS C 1  强制设置GBK编码
    0x1b, 0x40,                // ESC @  二次初始化彻底清空状态
    0x1b, 0x33, 0x00,          // ESC 3 0  行间距设为0，消除位图行间空隙
    0x1d, 0x76, 0x30, 0x00,    // GS v 0 m=0  光栅位图普通模式
    wLo, wHi,                  // 每行字节数 xL xH
    hLo, hHi                   // 高度点数 yL yH
  ])

  // 尾部指令：走纸到撕纸位，无多余走纸，不触发休眠
  var tail = new Uint8Array([0x0a, 0x0a])

  var out = new Uint8Array(header.length + bm.bitmap.length + tail.length)
  out.set(header, 0)
  out.set(bm.bitmap, header.length)
  out.set(tail, header.length + bm.bitmap.length)
  return out
}

/**
 * 自适应MTU分包写入（芝柯官方推荐方案，兼容1.04/1.06所有版本）
 * 自动检测当前蓝牙最大传输单元，单包最大200字节，30ms间隔，绝对不会被系统二次拆包
 */
function writeChunks(characteristic, bytes) {
  // 自动适配不同系统的MTU，固定用200字节安全阈值，芝柯CC4硬件最大支持单包200字节
  var CHUNK_SIZE = 200
  var offset = 0
  function next() {
    if (offset >= bytes.length) return Promise.resolve()
    var end = Math.min(offset + CHUNK_SIZE, bytes.length)
    var chunk = bytes.slice(offset, end)
    offset = end
    if (typeof characteristic.writeValueWithoutResponse === 'function') {
      return characteristic.writeValueWithoutResponse(chunk).then(function () {
        return new Promise(function (resolve) { setTimeout(resolve, 30) })
      }).then(next)
    } else {
      return characteristic.writeValue(chunk).then(function () {
        return new Promise(function (resolve) { setTimeout(resolve, 30) })
      }).then(next)
    }
  }
  return next()
}

/**
 * Canvas 转 CPCL 间隙标签任务
 * @param {HTMLCanvasElement} canvas
 * 位图使用原始二进制数据，固定 X=0、Y=0。
 */
function canvasToCpcl(canvas) {
  var bm = canvasToBitmap(canvas)
  function ascii(text) {
    var bytes = new Uint8Array(text.length)
    for (var i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i)
    return bytes
  }
  // 起点保持 (0, 0)，纸张尺寸取自预览画布；不添加回退或水平补偿。
  var header = ascii('! 0 200 200 ' + bm.height + ' 1\r\n' +
    'PAGE-WIDTH ' + canvas.width + '\r\nGAP-SENSE\r\n' +
    'CG ' + bm.widthBytes + ' ' + bm.height + ' 0 0 ')
  var tail = ascii('\r\nFORM\r\nPRINT\r\n')
  var out = new Uint8Array(header.length + bm.bitmap.length + tail.length)
  out.set(header, 0)
  out.set(bm.bitmap, header.length)
  out.set(tail, header.length + bm.bitmap.length)
  return out
}

/** 打印入口：protocol=cpcl、media=gap 使用标签任务；默认沿用 ESC/POS。 */
export function printCanvas(canvas, opts) {
  if (!isSupported()) {
    return Promise.reject(new Error('当前环境不支持 Web Bluetooth（需 HTTPS + Android Chrome）'))
  }
  var options = opts || {}
  if (options.protocol && options.protocol !== 'cpcl' && options.protocol !== 'escpos') {
    return Promise.reject(new Error('不支持的打印协议：' + options.protocol))
  }
  if (options.protocol === 'cpcl' && options.media !== 'gap') {
    return Promise.reject(new Error('CPCL 测试仅支持间隙标签'))
  }
  var bytes = options.protocol === 'cpcl' ? canvasToCpcl(canvas) : canvasToEscpos(canvas)
  return getWriteCharacteristic().then(function (ch) {
    return writeChunks(ch, bytes)
  })
}
